import { ApolloClient } from '@apollo/client'
import { FieldworkV2Query } from '../../../../data/gql-gen/questionnaire/graphql'
import { GET_FIELDWORK } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { ExportTarget } from '../../../../hooks/useDownloadSurveyDoc'
import { MarketOption } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { fieldworkLanguages } from '../../../../utils/fieldworkLanguages'
import { MASTER_LANGUAGE, primarySubtag } from './exportLanguageOptions'

/**
 * Every survey document of the project, for Download all: the master, then
 * each market (in `markets` order) once per language it fields in. Codes are
 * reduced to their primary subtag and de-duplicated in declared order;
 * English is the master text (no `languageCode`), and a market with no known
 * language gets its English doc. Non-English markets get no extra English
 * copy.
 */
export const downloadAllTargets = (
  markets: MarketOption[],
  languagesBySurveyId: Record<string, string[] | undefined>
): ExportTarget[] => {
  const targets: ExportTarget[] = [{}]
  for (const market of markets) {
    const codes = [
      ...new Set(
        (languagesBySurveyId[market.surveyId] ?? [])
          .map(primarySubtag)
          .filter(Boolean)
      )
    ]
    if (codes.length === 0) {
      targets.push({ market })
    }
    for (const code of codes) {
      targets.push(
        code === MASTER_LANGUAGE ? { market } : { market, languageCode: code }
      )
    }
  }
  return targets
}

/**
 * Each market's fieldwork languages, fetched fresh (at click time) so the
 * zip matches the current setup. Rejects if any market's fieldwork can't be
 * loaded: a market is never silently left out.
 */
export const loadMarketLanguages = async (
  client: ApolloClient<object>,
  markets: MarketOption[]
): Promise<Record<string, string[]>> => {
  const entries = await Promise.all(
    markets.map(async (market) => {
      const { data } = await client.query<FieldworkV2Query>({
        query: GET_FIELDWORK,
        context: { clientName: 'questionnaire' },
        variables: { surveyId: market.surveyId },
        fetchPolicy: 'network-only'
      })
      return [market.surveyId, fieldworkLanguages(data.fieldwork)] as const
    })
  )
  return Object.fromEntries(entries)
}
